import { Typography } from "@material-ui/core";
import { attributes, Collection, Events, Linked, memberOf } from "@type-r/models";
import { useIO, useLinked, useModel } from "@type-r/react";
import { localUserInfo } from 'api';
import { fetchResponse } from "api/endpoint";
import { RelationshipModel } from "api/relationship";
import { useModelCopySync } from "common";
import { CollapsableSection, colors, CompactTabs, div, Flex, Loading, Page, PageContainer, PageHeaderTile, PageTileField, PopupIODialog, PrimaryButton, SearchInput, SelectFile, SmallTile, Tab, TilesList, Toolbar, useLinkedQueryParam } from "lqx-react";
import * as React from "react";
import { useMemo } from "react";
import { Circles, RowH3Bold } from "../../../../common/inmatch";
import { canEdit, SelectedCpTab, showTab } from "./common/api";
import { CpAllowableDiscountsAndTolerances } from "./tabs/cp-allowable-discounts";
import { CpExternalContacts } from "./tabs/cp-external-contacts";
import { CpInvoicing } from "./tabs/cp-invoicing";
import { CpPaymentOfRaProfile } from "./tabs/cp-payment";
import { CpStatementOfAccount } from "./tabs/cp-statement";

const State = attributes({
  cpRelationships: [RelationshipModel],
  selected: memberOf('cpRelationships', RelationshipModel),
  searchFilter: '',
  selectedTabIndex: 0,
  sortBy: {
    name: "Customer.companyName",
    isAsc: true
  },
  isPartialLoad: true
})

const compareUsingCpRules = (rel: RelationshipModel, filter: string): Boolean => {
  return !!rel.Customer
    && (rel.Customer.companyName.toLowerCase().indexOf(filter) >= 0
      || rel.Customer.externalRelationshipId.toLowerCase().indexOf(filter) >= 0);
}

export const CustomerProfilePage = () => {

  const state = useModel(State);
  const $relationshipId = useLinkedQueryParam("relationshipId");

  const isReady = useIO(async () => {
    await state.cpRelationships.fetch({
      params: { partial: state.isPartialLoad }
    });
    state.selected = $relationshipId.value ? state.cpRelationships.find(x => x.id == $relationshipId.value) : null;
  });

  const filterUserRelationships = (relationships: Collection<RelationshipModel>) => {
    let data = relationships.filter(x => !!x.Customer && !!x.Supplier);

    if (localUserInfo.isAdminRead || localUserInfo.isAdminWrite)
      return data;

    let userSupplierOnlyRel = localUserInfo.relationships_user_info.filter(x => x.role === 'Supplier');
    let intersection = data.filter(x => userSupplierOnlyRel.some(y => x.id == y.relationshipId.toString()));

    return intersection;
  }

  const byCustomerCompanyName = (a: RelationshipModel, b: RelationshipModel) => {
    return a.Customer && b.Customer && (a.Customer.companyName > b.Customer.companyName) ? 1 : ((a.Customer && b.Customer && b.Customer.companyName > a.Customer.companyName) ? -1 : 0);
  }

  const filtered = useMemo(() => {
    state.selected = null;

    let relationshipData = filterUserRelationships(state.cpRelationships);

    if (state.searchFilter) {
      let lowered = state.searchFilter.toLowerCase();
      return relationshipData
        .filter(x => compareUsingCpRules(x, lowered))
        .sort(byCustomerCompanyName);
    }

    return relationshipData.sort(byCustomerCompanyName);

  }, [state.searchFilter, state.cpRelationships['_changeToken']]);

  return (
    <PageContainer>
      <Page
        menuWidth={530}
        maxWidth="xl"
        left={
          <>
            <div style={{ display: 'inline' }}>
              <CustomerProfileExportButton />
              <CustomerProfileImportButton />
            </div>
            <Toolbar>
              <SearchInput
                $value={state.$.searchFilter}
                placeholder="Filter by Customer Name, Customer External ID"
              />
            </Toolbar>
            <VerticalSpace />
            {!isReady ?
              <Loading />
              :
              filtered.length ?
                <TilesList
                  items={filtered}
                  itemSize={64}
                  renderer={CustomerProfileTile(state.$.selected)}
                  selectedItem={state.selected}
                />
                :
                <Flex center stretch>
                  <Typography variant="h4">No results found.</Typography>
                </Flex>
            }
          </>
        }>
        <>
          {isReady && state.selected ?
            <CustomerProfileDetails key={state.selected.cid} selected={state.selected} />
            :
            <Flex center stretch>
              Select the customer
            </Flex>
          }
        </>
      </Page>
    </PageContainer>
  );
};

const VerticalSpace = div({
  height: 8
})

const CustomerProfileTile = $selected => ({ data, style, index }) => {
  const relationship = data.items[index] as RelationshipModel;

  return (
    <div style={style}>
      <SmallTile
        color={'green'}
        isSelected={$selected.value === relationship}
        onClick={e => {
          e.stopPropagation();
          $selected.set(relationship);
        }}
        footer={
          <>
            <div>{relationship.Customer.externalRelationshipId}</div>
            <Flex grow />
          </>
        }
      >
        {relationship.Customer.companyName}
      </SmallTile>
    </div>
  )
}

const setDefaultTab = (id: any) => {
  const $selectedCpTab = useLinkedQueryParam("selectedCpTab");
  const $tabFromQueryParams = $selectedCpTab.value ? $selectedCpTab.value as SelectedCpTab : SelectedCpTab.Invoicing;

  return showTab(id, $tabFromQueryParams) ? $tabFromQueryParams.toString() :
    showTab(id, SelectedCpTab.StatementOfAccount) ? SelectedCpTab.StatementOfAccount.toString() :
      showTab(id, SelectedCpTab.PaymentAndRaProfile) ? SelectedCpTab.PaymentAndRaProfile.toString() :
        showTab(id, SelectedCpTab.AllowableDiscountsAndTolerances) ? SelectedCpTab.AllowableDiscountsAndTolerances.toString() : '';
}

export const CustomerProfileDetails = ({ selected }: {
  selected: RelationshipModel
}) => {

  const $relationshipId = useLinkedQueryParam("relationshipId");

  if ($relationshipId.value)
    $relationshipId.set(selected.id);

  const local = useModelCopySync(selected);
  const $selectedTab = useLinked(setDefaultTab(local.id));

  useMemo(() => {
    const $selectedCpTab = useLinkedQueryParam("selectedCpTab");

    if ($selectedCpTab.value)
      $selectedCpTab.set($selectedTab.value);

  }, [$selectedTab['_changeToken']]);

  return (
    local.Customer && local.Supplier ?
      <>
        <PageHeaderTile
          color={colors["green"]}
          title={
            <Flex row alignCenter>
              <RowH3Bold>{local.Supplier.companyName}</RowH3Bold>
              <Circles />
              <RowH3Bold>{local.Customer.companyName}</RowH3Bold>
            </Flex>
          }
        >
          <Flex row wrap>
            <PageTileField label="Business Partner Code">
              {local.Customer.externalRelationshipId}
            </PageTileField>
            <PageTileField label="Site Location">
              {local.gbsUnit}
            </PageTileField>
            <PageTileField label="Parent Site">
              {local.Supplier.mep}
            </PageTileField>
            <PageTileField label="Sub Site">
              {local.Supplier.subMep}
            </PageTileField>
          </Flex>
        </PageHeaderTile>
        <CollapsableSection
          qa-id="cpDetails"
          title={
            <CompactTabs $value={$selectedTab}>
              {showTab(+local.id, SelectedCpTab.Invoicing) ? <Tab value={SelectedCpTab.Invoicing}>Invoicing</Tab> : <></>}
              {showTab(+local.id, SelectedCpTab.StatementOfAccount) ? <Tab value={SelectedCpTab.StatementOfAccount}>Statement Of Account</Tab> : <></>}
              {showTab(+local.id, SelectedCpTab.PaymentAndRaProfile) ? <Tab value={SelectedCpTab.PaymentAndRaProfile}>Payment and RA Profile</Tab> : <></>}
              {showTab(+local.id, SelectedCpTab.AllowableDiscountsAndTolerances) ? <Tab value={SelectedCpTab.AllowableDiscountsAndTolerances}>Allowable Discounts & Tolerances</Tab> : <></>}
              {showTab(+local.id, SelectedCpTab.ExternalContacts) ? <Tab value={SelectedCpTab.ExternalContacts}>Contacts External</Tab> : <></>}
            </CompactTabs>
          }
        >
          {
            $selectedTab.value === SelectedCpTab.Invoicing ? <CpInvoicing $selected={local} canEdit={canEdit(+local.id, $selectedTab.value)}></CpInvoicing> :
              $selectedTab.value === SelectedCpTab.StatementOfAccount ? <CpStatementOfAccount $selected={local} canEdit={canEdit(+local.id, $selectedTab.value)}></CpStatementOfAccount> :
                $selectedTab.value === SelectedCpTab.PaymentAndRaProfile ? <CpPaymentOfRaProfile $selected={local} canEdit={canEdit(+local.id, $selectedTab.value)}></CpPaymentOfRaProfile> :
                  $selectedTab.value === SelectedCpTab.AllowableDiscountsAndTolerances ? <CpAllowableDiscountsAndTolerances $selected={local} canEdit={canEdit(+local.id, $selectedTab.value)}></CpAllowableDiscountsAndTolerances> :
                    $selectedTab.value === SelectedCpTab.ExternalContacts ? <CpExternalContacts $selected={local} canEdit={canEdit(+local.id, $selectedTab.value)}></CpExternalContacts> :
                      <></>
          }

        </CollapsableSection>
      </>
      : <Flex center stretch> Select the customer </Flex>
  )
}

export const CustomerProfileExportButton = () => {
  const { isSupplier, isSuperSupplier } = localUserInfo;

  const $isOpen = useLinked(false);

  return (
    isSupplier || isSuperSupplier ?
      <>
        <PrimaryButton color="blue" onClick={$isOpen.true} style={{ width: 80, marginBottom: 6 }} >Export</PrimaryButton>
        {$isOpen.value ? <CustomerProfileExportDialog $isOpen={$isOpen} /> : null}
      </> : null
  )
};

const CustomerProfileExportDialog = ({ $isOpen }: {
  $isOpen: Linked<boolean>
}) => {

  const Export = async () => {
    const url = '/api/inblock/tradetalk/v2/Relationship/customer-profile/export';
    var result = await fetchResponse('GET', url);
    const imageBlog = await result.blob();
    const imageURL = URL.createObjectURL(imageBlog);

    const anchor = document.createElement("a");
    anchor.href = imageURL;
    var filename = result.headers.get('content-disposition').match(/(?<=filename\=).*(?=;)/g);
    anchor.download = (filename as any) || "CustomerProfiles.xlsx";

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  return (
    <PopupIODialog
      title="Export"
      $isOpen={$isOpen}
      actionLabel="Yes"
      success="Success!"
      doIO={Export}
      hideTitleOnSuccess
    >
      Do you want to export all Customer Profiles?
    </PopupIODialog>
  );
};

export const CustomerProfileImportButton = () => {
  const { isSupplier, isSuperSupplier } = localUserInfo;

  const $isOpen = useLinked(false);

  return (
    isSupplier || isSuperSupplier ?
      <>
        <PrimaryButton color="blue" onClick={$isOpen.true} style={{ width: 80, marginBottom: 6 }} >Import</PrimaryButton>
        {$isOpen.value ? <CustomerProfileImportDialog $isOpen={$isOpen} /> : null}
      </> : null
  )
};

const CustomerProfileImportDialog = ({ $isOpen }: {
  $isOpen: Linked<boolean>
}) => {
  const $file = useLinked(null);
  const file = $file.value;

  let response = useLinked(null);

  const Import = async () => {
    const url = '/api/inblock/tradetalk/v2/Relationship/customer-profile/import';

    const formData = new FormData();
    formData.append("upfile", file, file.name);

    var resp = await fetch(url, {
      method: 'POST',
      headers: new Headers({
        "Authorization": "Bearer " + localUserInfo.token
      }),
      body: formData
    });

    var respJson = await resp.json()

    Events.trigger('cpImport:done');

    response.set(respJson);
  }

  return (
    <PopupIODialog
      title="Import Customer Profile"
      $isOpen={$isOpen}
      actionLabel="Yes"
      success={<ImportResponse response={response} />}
      disabled={!file}
      doIO={Import}
    >
      Please select the file to import.

      <SelectFile accept=".xlsx" label="Attachment" $value={$file} />

    </PopupIODialog>
  );
};

const ImportResponse = ({ response }: { response: Linked<any> }) => {
  return (
    <>
      {
        response && response.value ?
          <>
            {response.value.validationResult.successCount ? <p>({response.value.validationResult.successCount}) Relationships updated succesfully</p> : null}
            <ImportErrorList errors={response.value.validationResult.globalErrors} message={'Relationships updates failed'}></ImportErrorList>
            <ImportErrorList errors={response.value.validationResult.invocingErrors} message={'Relationships Invocing updates failed'}></ImportErrorList>
            <ImportErrorList errors={response.value.validationResult.statementErrors} message={' Relationships Statement updates failed'}></ImportErrorList>
            <ImportErrorList errors={response.value.validationResult.paymentErrors} message={'Relationships Payment updates failed'}></ImportErrorList>
            <ImportErrorList errors={response.value.validationResult.externalContactsErrors} message={'Relationships External Contact updates failed'}></ImportErrorList>
          </>
          : <></>
      }
    </>
  );
}

const ImportErrorList = ({ errors, message }: { errors: any[], message: String }) => {

  return (
    errors && errors.length
      ? <>
        <p >({errors.length}) {message} ( {errors.join(', ')} )</p>
      </>
      : <></>
  );
}